import { useState } from "react";
import { Mail, ShieldCheck, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CONTACT } from "@/lib/testIds";
import { CONTACT_CONTENT } from "@/data/staticContent";
import { frontendApi, isTransportError } from "@/lib/api";

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export const Contact = () => {
  const c = CONTACT_CONTENT;
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please tell us your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.subject) e.subject = "Pick a subject.";
    if (form.message.trim().length < 10)
      e.message = "A little more context helps us reply well.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await frontendApi.contact(form);
      setSuccess(true);
      setForm(initial);
    } catch (err) {
      if (isTransportError(err)) {
        // API unreachable — still show the thank-you state in demo mode.
        setSuccess(true);
        setForm(initial);
      } else {
        const laravelErrors =
          err?.response?.data?.errors || err?.apiErrors || {};
        const mapped = {};
        for (const [k, v] of Object.entries(laravelErrors)) {
          mapped[k] = Array.isArray(v) ? v[0] : v;
        }
        if (Object.keys(mapped).length) {
          setErrors(mapped);
        } else {
          toast.error(
            err?.response?.data?.message ||
              err?.apiMessage ||
              "Could not send. Please try again shortly.",
          );
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid={CONTACT.root} className="pb-24">
      <section className="mx-auto max-w-[1400px] px-5 pt-20 md:px-10 md:pt-32">
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground md:text-xs">
          {c.eyebrow}
        </p>
        <h1 className="mt-5 font-display text-5xl font-medium leading-[0.95] tracking-tight md:mt-8 md:text-[8rem]">
          Say <span className="italic">hello</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground/80 md:mt-10 md:text-lg">
          {c.lede}
        </p>
      </section>

      <section className="mx-auto mt-16 grid max-w-[1400px] grid-cols-1 gap-12 px-5 md:mt-24 md:grid-cols-[1.4fr_1fr] md:gap-20 md:px-10">
        {/* Form */}
        <div>
          {success ? (
            <div
              data-testid={CONTACT.success}
              className="border border-foreground/15 p-10 md:p-14"
            >
              <p className="text-[10px] uppercase tracking-[0.2em] text-primary md:text-xs">
                Received · thank you
              </p>
              <h2 className="mt-4 font-display text-3xl font-medium leading-tight tracking-tight md:text-5xl">
                We'll be in touch,
                <br />
                <span className="italic">soon</span>.
              </h2>
              <p className="mt-6 text-base leading-relaxed text-muted-foreground">
                We reply to most messages within a working day. If it's urgent,
                mention that in the subject line and we'll bump it.
              </p>
              <button
                type="button"
                onClick={() => setSuccess(false)}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-foreground/30 px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:border-foreground"
              >
                Send another
              </button>
            </div>
          ) : (
            <form
              data-testid={CONTACT.form}
              onSubmit={onSubmit}
              className="space-y-8"
              noValidate
            >
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <Field
                  label="Your name"
                  required
                  error={errors.name}
                  data-testid={CONTACT.name}
                  value={form.name}
                  onChange={(v) => set("name", v)}
                />
                <Field
                  label="Email"
                  required
                  type="email"
                  error={errors.email}
                  data-testid={CONTACT.email}
                  value={form.email}
                  onChange={(v) => set("email", v)}
                />
              </div>
              <Field
                label="Phone (optional)"
                type="tel"
                data-testid={CONTACT.phone}
                value={form.phone}
                onChange={(v) => set("phone", v)}
              />
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                  Subject
                  <span className="ml-1 text-primary">*</span>
                </label>
                <select
                  data-testid={CONTACT.subject}
                  value={form.subject}
                  onChange={(e) => set("subject", e.target.value)}
                  className="mt-3 w-full border-b border-foreground/20 bg-transparent py-3 text-base text-foreground focus:border-foreground focus:outline-none"
                >
                  <option value="">Choose one</option>
                  {c.subjects.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {errors.subject && (
                  <p className="mt-2 text-xs text-primary">{errors.subject}</p>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
                  Message
                  <span className="ml-1 text-primary">*</span>
                </label>
                <textarea
                  data-testid={CONTACT.message}
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  rows={5}
                  className="mt-3 w-full resize-none border-b border-foreground/20 bg-transparent py-3 text-base text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none"
                  placeholder="Tell us a little bit — dates, destinations, what you're looking for."
                />
                {errors.message && (
                  <p className="mt-2 text-xs text-primary">{errors.message}</p>
                )}
              </div>

              <div className="flex flex-col items-start gap-6 border-t border-foreground/10 pt-8 md:flex-row md:items-center md:justify-between">
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ShieldCheck size={14} className="text-primary" />
                  Protected by reCAPTCHA. Read our{" "}
                  <a href="/terms-and-conditions" className="underline">
                    privacy terms
                  </a>
                  .
                </p>
                <button
                  type="submit"
                  data-testid={CONTACT.submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send message"}{" "}
                  <ArrowRight size={16} />
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Channels */}
        <aside className="md:pt-6">
          <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground md:text-xs">
            Direct email
          </p>
          <div className="mt-4 space-y-5">
            {c.channels.map((ch) => (
              <a
                key={ch.value}
                href={`mailto:${ch.value}`}
                className="group flex items-center justify-between border-b border-foreground/10 pb-4 transition-colors hover:border-foreground/40"
              >
                <div>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {ch.label}
                  </p>
                  <p className="mt-1 font-display text-lg tracking-tight text-foreground md:text-xl">
                    {ch.value}
                  </p>
                </div>
                <Mail
                  size={16}
                  className="text-muted-foreground transition-colors group-hover:text-primary"
                />
              </a>
            ))}
          </div>
          <div className="mt-10 border border-foreground/10 p-6">
            <p className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Reply time
            </p>
            <p className="mt-3 font-display text-2xl leading-tight tracking-tight">
              Usually within 24 hours,
              <br />
              often sooner.
            </p>
          </div>
        </aside>
      </section>
    </main>
  );
};

const Field = ({ label, required, type = "text", error, value, onChange, ...rest }) => (
  <div>
    <label className="block text-[10px] font-medium uppercase tracking-[0.2em] text-muted-foreground">
      {label}
      {required && <span className="ml-1 text-primary">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-3 w-full border-b border-foreground/20 bg-transparent py-3 text-base text-foreground placeholder:text-muted-foreground focus:border-foreground focus:outline-none"
      {...rest}
    />
    {error && <p className="mt-2 text-xs text-primary">{error}</p>}
  </div>
);

export default Contact;
